/*
 * This file is part of the Web3 Library developed by mFactory GmbH.
 *
 * Copyright © 2021, mFactory GmbH
 *
 * Solana Reference Stake Pool is free software: you can redistribute it
 * and/or modify it under the terms of the GNU Affero General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Solana Reference Stake Pool is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.
 * If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
 *
 * You can be released from the requirements of the Affero GNU General Public License
 * by purchasing a commercial license. The purchase of such a license is
 * mandatory as soon as you develop commercial activities using the
 * Solana Reference Stake Pool code without disclosing the source code of
 * your own applications.
 *
 * The developer of this program can be contacted at <info@mfactory.ch>.
 */

/**
 * Like lodash.debounce,
 * but also avoids async invocations to overlap
 *
 * @param callback
 * @param wait
 * @param leading
 * @param maxWait
 */
export function debounceAsync<CB extends (...args: any[]) => Promise<R>, R>(
  callback: CB,
  wait = 100,
  { leading = false, maxWait = Number.POSITIVE_INFINITY } = {},
) {
  let started = 0 // latest callback invocation
  let runningCallback: Promise<R> | undefined // latest callback invocation result
  let runningDebouncer: Promise<R | undefined> // latest wrapper invocation
  let waitingSince = 0 // we are delaying invocation since
  let whoIsWaiting: undefined | any[] // args' array object identifies the pending instance, and incidentally stores args

  const interceptingWrapper = (...args: any[]) => (runningDebouncer = debouncer(...args))

  return Object.assign(interceptingWrapper, {
    cancel: () => (whoIsWaiting = undefined),
    flush() {
      return whoIsWaiting ? callback(...whoIsWaiting) : this.cancel()
    },
  })

  async function debouncer(...args: any[]) {
    whoIsWaiting = args
    waitingSince ||= Date.now()
    await runningCallback
    const waitingCap = maxWait - (Date.now() - (waitingSince || started))
    const waitFor = Math.min(waitingCap, leading ? wait - (Date.now() - started) : wait)
    if (waitFor > 0) {
      await new Promise(resolve => setTimeout(resolve, waitFor))
    }
    if (!whoIsWaiting) {
      // canceled

      return void (waitingSince = 0)
    }
    if (whoIsWaiting !== args) {
      // another fresher call is waiting
      return runningDebouncer
    }
    waitingSince = 0
    whoIsWaiting = undefined
    started = Date.now()
    try {
      runningCallback = callback(...args)
      return await runningCallback
    } finally {
      runningCallback = undefined
    }
  }
}
